import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCatalog } from "../../context/CatalogContext";
import DesignCard from "../DesignCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (p) =>
  !p ? "" : p.startsWith("http") ? p : `${BACKEND_URL}${p}`;

/**
 * Limited-time spotlight strip — Raksha Bandhan, Diwali, Wedding week, etc.
 * Enabled collections render at the TOP of the homepage (right below the
 * hero) so they're the first thing shoppers see. Admin controls priority
 * and can toggle each on/off from the Limited Collections admin tab.
 */
export default function LimitedCollections() {
  const catalog = useCatalog();
  const {
    limitedCollections = [],
    customDesigns = [],
    limitedScopedDesigns = [],
    getDesignsMerged,
  } = catalog;

  // Resolve each collection's designIds into hydrated design objects. The
  // pool covers three sources so an admin can pick from anywhere:
  //   1. Collection-scoped designs (created inside this collection editor)
  //   2. Existing custom designs (added via the picker)
  //   3. Default catalog designs (added by pasting/selecting an id)
  const collections = useMemo(() => {
    if (!limitedCollections.length) return [];
    const pool = new Map();
    limitedScopedDesigns.forEach((d) => pool.set(d.id, d));
    customDesigns.forEach((d) => { if (!pool.has(d.id)) pool.set(d.id, d); });
    // Include default catalog too — cheap because we only need referenced ids.
    const referenced = new Set();
    limitedCollections.forEach((c) => (c.designIds || []).forEach((id) => referenced.add(id)));
    if (referenced.size && getDesignsMerged) {
      const all = catalog.categoriesMerged.flatMap((cat) =>
        cat.subCategories.flatMap((sub) => getDesignsMerged(cat.slug, sub.slug))
      );
      all.forEach((d) => { if (!pool.has(d.id)) pool.set(d.id, d); });
    }
    return limitedCollections.map((c) => ({
      ...c,
      designs: (c.designIds || []).map((id) => pool.get(id)).filter(Boolean),
    }));
  }, [limitedCollections, customDesigns, limitedScopedDesigns, getDesignsMerged, catalog.categoriesMerged]);

  if (!collections.length) return null;

  return (
    <section className="ltc" data-testid="limited-collections-section">
      <div className="wrap">
        {collections.map((c, idx) => (
          <motion.div
            key={c.id}
            className="ltc-block"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`ltc-block-${c.id}`}
          >
            <header className="ltc-head">
              <div className="ltc-head-copy">
                <span className="ltc-badge">Limited time</span>
                <h2>
                  <Link to={`/limited/${c.id}`} className="ltc-title-link" data-testid={`ltc-title-link-${c.id}`}>
                    {c.name}
                  </Link>
                </h2>
                {c.tagline && <p>{c.tagline}</p>}
                <Link to={`/limited/${c.id}`} className="ltc-view-all" data-testid={`ltc-view-all-${c.id}`}>
                  View full collection →
                </Link>
              </div>
              {c.cover && (
                <Link to={`/limited/${c.id}`} className="ltc-cover" aria-hidden>
                  <img src={absolutize(c.cover)} alt="" loading="lazy" />
                </Link>
              )}
            </header>

            {c.designs.length > 0 ? (
              <div className="design-grid ltc-grid" data-testid={`ltc-grid-${c.id}`}>
                {c.designs.slice(0, 8).map((d) => (
                  <DesignCard key={d.id} design={d} />
                ))}
              </div>
            ) : (
              <p className="ltc-empty">
                Curated pieces coming soon — the collection is being finalised in-store.
              </p>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
